import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useFetchers, useLocation, useRouteLoaderData } from 'react-router'
import {
	getAnalyticsConfig,
	type AnalyticsEnv,
} from '#app/config/analytics/analytics.config'
import {
	emitIdentify,
	emitAnalyticsActionResult,
	emitAnalyticsFlashEvent,
	emitPageView,
	emitResetIdentity,
} from '#app/utils/pz/analytics'
import { type AnalyticsPageType } from '#app/utils/pz/analytics.catalog'
import {
	CookieBannerComponent,
	useCookieConsent,
} from '#app/utils/pz/cookieBanner'
import { useTagManager } from '#app/utils/pz/ui/tagManager'

type RootAnalyticsData = {
	ENV?: AnalyticsEnv
	locale?: string
	user?: {
		id?: string | null
		analyticsExcluded?: boolean
	} | null
}

const identifiedUserStorageKey = 'analytics_identified_user_id'
const useBrowserLayoutEffect =
	typeof window === 'undefined' ? useEffect : useLayoutEffect

export function AnalyticsBootstrap({
	initialConsent,
	pageType,
	showCookieBanner = false,
}: {
	initialConsent: boolean
	pageType: AnalyticsPageType
	showCookieBanner?: boolean
}) {
	const rootData = useRouteLoaderData('root') as RootAnalyticsData | undefined
	const [isMounted, setIsMounted] = useState(false)
	const analytics = getAnalyticsConfig({
		...(rootData?.ENV ?? {}),
		MODE: rootData?.ENV?.MODE,
	})
	const location = useLocation()
	const fetchers = useFetchers()
	const { consentGiven, handleAccept, handleDecline } =
		useCookieConsent(initialConsent)
	const lastPageViewKeyRef = useRef<string | null>(null)
	const lastIdentifiedUserIdRef = useRef<string | null>(null)
	const processedActionResultsRef = useRef(new WeakSet<object>())
	const locale = rootData?.locale
	const userId = rootData?.user?.id ?? null
	const internalOrTestUser =
		rootData?.user?.analyticsExcluded === true ||
		rootData?.ENV?.ANALYTICS_EXCLUDED === 'true'

	useEffect(() => {
		setIsMounted(true)
	}, [])

	useTagManager(analytics.tagManagerId, consentGiven, {
		dataLayer: analytics.dataLayer,
		enableConsentMode: true,
		enabled: analytics.enabled,
	})

	// A redirect can set the flash cookie without changing the user, as in
	// /signup -> /verify, so every navigation checks for a pending event.
	const routeKey = `${location.pathname}${location.search}`
	useEffect(() => {
		if (!analytics.enabled || !consentGiven) return
		emitAnalyticsFlashEvent({
			userId,
			internalOrTestUser,
		})
	}, [analytics.enabled, consentGiven, userId, internalOrTestUser, routeKey])

	useEffect(() => {
		if (!analytics.enabled || !consentGiven) return

		const pageViewKey = `${location.pathname}${location.search}`
		if (lastPageViewKeyRef.current === pageViewKey) return
		lastPageViewKeyRef.current = pageViewKey

		emitPageView({
			pageType,
			properties: {
				locale,
			},
		})
	}, [
		analytics.enabled,
		consentGiven,
		location.pathname,
		location.search,
		locale,
		pageType,
	])

	useBrowserLayoutEffect(() => {
		if (!analytics.enabled || !consentGiven) return

		const storedUserId = window.sessionStorage.getItem(identifiedUserStorageKey)
		if (!userId) {
			if (!storedUserId) return
			emitResetIdentity()
			window.sessionStorage.removeItem(identifiedUserStorageKey)
			lastIdentifiedUserIdRef.current = null
			return
		}

		if (storedUserId && storedUserId !== userId) {
			emitResetIdentity()
		}
		if (storedUserId === userId || lastIdentifiedUserIdRef.current === userId) {
			return
		}

		emitIdentify({ userId }, { internalOrTestUser })
		window.sessionStorage.setItem(identifiedUserStorageKey, userId)
		lastIdentifiedUserIdRef.current = userId
	}, [analytics.enabled, consentGiven, userId, internalOrTestUser])

	useEffect(() => {
		if (!analytics.enabled || !consentGiven) return

		for (const fetcher of fetchers) {
			const result = fetcher.data
			if (!result || typeof result !== 'object') continue
			if (processedActionResultsRef.current.has(result)) continue
			processedActionResultsRef.current.add(result)
			emitAnalyticsActionResult(result)
		}
	}, [analytics.enabled, consentGiven, fetchers])

	if (!showCookieBanner || !isMounted) return null

	return (
		<CookieBannerComponent onAccept={handleAccept} onDecline={handleDecline} />
	)
}
